import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Building2, Check, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuildingAssignmentModal = ({ member, token, onClose, onSave }) => {
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [assignedBuildingIds, setAssignedBuildingIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [member]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch available buildings
      const buildingsRes = await axios.get(`${API}/equipe/available-buildings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableBuildings(buildingsRes.data || []);
      
      // Fetch currently assigned buildings for this member
      if (member.id || member.user_id) {
        const userId = member.user_id || member.id;
        try {
          const assignedRes = await axios.get(`${API}/equipe/${userId}/buildings`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAssignedBuildingIds(assignedRes.data?.building_ids || []);
        } catch (err) {
          // If endpoint fails, use member's building_ids directly
          setAssignedBuildingIds(member.building_ids || []);
        }
      } else {
        setAssignedBuildingIds(member.building_ids || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleBuilding = (buildingId) => {
    setAssignedBuildingIds(prev => 
      prev.includes(buildingId)
        ? prev.filter(id => id !== buildingId)
        : [...prev, buildingId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const userId = member.user_id || member.id;
      
      await axios.put(
        `${API}/equipe/${userId}/buildings`,
        { building_ids: assignedBuildingIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      onSave && onSave(assignedBuildingIds);
      onClose();
    } catch (error) {
      console.error('Error saving building assignments:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const filteredBuildings = availableBuildings.filter(b => {
    const search = searchTerm.toLowerCase();
    return (
      (b.nom || '').toLowerCase().includes(search) ||
      (b.adresse || '').toLowerCase().includes(search) ||
      (b.ville || '').toLowerCase().includes(search)
    );
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm" data-testid="building-assignment-modal">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00D3A8]/10 rounded-lg">
              <Building2 className="h-5 w-5 text-[#00D3A8]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Assigner des immeubles</h2>
              <p className="text-sm text-gray-500">
                {member.prenom} {member.nom} • {member.role || 'Assistant'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-modal-btn"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Rechercher un immeuble..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none transition-all"
            data-testid="search-buildings-input"
          />
        </div>

        {/* Buildings List */}
        <div className="flex-1 overflow-y-auto border border-gray-100 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
            </div>
          ) : filteredBuildings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Building2 size={40} className="mx-auto mb-3 text-gray-300" />
              <p>{searchTerm ? 'Aucun immeuble trouvé' : 'Aucun immeuble disponible'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredBuildings.map((building) => {
                const isAssigned = assignedBuildingIds.includes(building.id);
                return (
                  <div
                    key={building.id}
                    onClick={() => toggleBuilding(building.id)}
                    className={`flex items-center gap-4 p-4 cursor-pointer transition-all hover:bg-gray-50 ${
                      isAssigned ? 'bg-[#00D3A8]/5' : ''
                    }`}
                    data-testid={`building-item-${building.id}`}
                  >
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
                      isAssigned 
                        ? 'bg-[#00D3A8] border-[#00D3A8]' 
                        : 'border-gray-300 hover:border-[#00D3A8]'
                    }`}>
                      {isAssigned && <Check size={14} className="text-white" />}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{building.nom}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {building.adresse}{building.code_postal ? `, ${building.code_postal}` : ''} {building.ville}
                      </p>
                    </div>
                    
                    {building.nb_lots > 0 && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                        {building.nb_lots} lots
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {assignedBuildingIds.length} immeuble(s) sélectionné(s)
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-all font-medium"
              data-testid="cancel-assignment-btn"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white rounded-full hover:opacity-90 transition-all font-medium shadow-md disabled:opacity-50"
              data-testid="save-assignment-btn"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildingAssignmentModal;
